import { __rest } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { flexRender } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { Checkbox } from '../../../checkbox/src';
import { Message, useLocalizeMessage } from '../../../i18n/src';
import { Icon } from '../../../icon/src';
import { RadioGroup, RadioGroupOption } from '../../../radio_group/src';
import { Skeleton } from '../../../skeleton/src';
import { Spinner } from '../../../spinner/src';
import { Tooltip } from '../../../tooltip/src';
import { TableHeadRow } from './table_head';
import { TableRowHasNoResults } from './table_row';
/** Number of skeleton rows rendered while the next page is being fetched. */
const LOADING_MORE_SKELETON_COUNT = 3;
/** Number of skeleton rows rendered for the initial load. */
const INITIAL_SKELETON_COUNT = 10;
/** Default prefetch distance (px) from the bottom that triggers `onLoadMore`. */
const DEFAULT_LOAD_MORE_THRESHOLD = 800;
/** Default overscan distance (px) rendered outside the viewport in both directions. */
const DEFAULT_OVERSCAN_DISTANCE = 300;
const CELL_CLASSES = 'nucleus-box-border nucleus-h-15 nucleus-border-solid nucleus-border-0 nucleus-border-b-1 nucleus-border-neutral-300 nucleus-px-6 nucleus-py-2 nucleus-text-left nucleus-text-sm';
const CONTROL_CELL_CLASSES = 'nucleus-box-border nucleus-border-solid nucleus-border-0 nucleus-border-b-1 nucleus-border-neutral-300 nucleus-px-4';
/** Reads statically-provided expanded content from a row (the async path stores resolved content separately). */
const getStaticExpandableContent = (row, expandableContentField) => {
    if (expandableContentField &&
        row.original &&
        typeof row.original === 'object' &&
        expandableContentField in row.original) {
        const content = row.original[expandableContentField];
        if (content !== undefined && content !== null) {
            return content;
        }
    }
    return undefined;
};
/**
 * Virtualized table body backed by react-virtuoso's `TableVirtuoso`. Renders only the rows in (and near) the viewport
 * and measures their heights automatically, while reusing the same `@tanstack/react-table` instance so sorting,
 * selection, and filtering keep working. Pair with `onLoadMore` for infinite scrolling.
 */
export const VirtualTableBody = ({ table, rows, colSpan, isSelectable, isSingleSelect, enableSorting, labelNoResults, highlightedRowIds = [], getRowCustomClasses, disableRowSelectionReason, isLoading = false, hasExpandableRows, expandedRowIds, loadingRowIds, asyncContentMap, onToggleExpand, getExpandedContentClasses, rowHasExpandableContent, expandableContentField, onLoadMore, hasNextPage, isLoadingMore, loadMoreThreshold = DEFAULT_LOAD_MORE_THRESHOLD, overscanDistance = DEFAULT_OVERSCAN_DISTANCE, customScrollParent, autoColumnWidths = false, }) => {
    const localizeMessage = useLocalizeMessage();
    // Precompute sticky left offsets for left-pinned columns (column-level, not row-level).
    const leftPinOffsets = useMemo(() => {
        const offsets = new Map();
        if (!rows.length) {
            return offsets;
        }
        const pinned = rows[0]
            .getVisibleCells()
            .filter((c) => c.column.getIsPinned() === 'left')
            .sort((a, b) => a.column.getPinnedIndex() - b.column.getPinnedIndex());
        let acc = 0;
        pinned.forEach((c) => {
            offsets.set(c.column.id, acc);
            acc += c.column.getSize();
        });
        return offsets;
    }, [rows]);
    const items = useMemo(() => {
        if (isLoading) {
            return Array.from({ length: INITIAL_SKELETON_COUNT }, (_, index) => ({ kind: 'skeleton', id: `init-${index}` }));
        }
        const list = [];
        rows.forEach((row) => {
            list.push({ kind: 'row', row });
            if (hasExpandableRows && (expandedRowIds === null || expandedRowIds === void 0 ? void 0 : expandedRowIds.has(row.id))) {
                list.push({ kind: 'expanded', row });
            }
        });
        if (isLoadingMore) {
            for (let index = 0; index < LOADING_MORE_SKELETON_COUNT; index += 1) {
                list.push({ kind: 'skeleton', id: `more-${index}` });
            }
        }
        return list;
    }, [isLoading, rows, hasExpandableRows, expandedRowIds, isLoadingMore]);
    const handleEndReached = useCallback(() => {
        if (!isLoading && onLoadMore && hasNextPage && !isLoadingMore) {
            onLoadMore();
        }
    }, [isLoading, onLoadMore, hasNextPage, isLoadingMore]);
    const renderSkeletonCells = useCallback(() => Array.from({ length: colSpan }).map((_, cellIndex) => (_jsx("td", { className: `${CELL_CLASSES} nucleus-font-normal`, children: _jsx(Skeleton, { variant: 'line', height: '0.75rem' }) }, `skeleton-cell-${cellIndex}`))), [colSpan]);
    const renderSelectionCell = useCallback((row) => {
        if (!isSelectable) {
            return null;
        }
        const isDisabled = !row.getCanSelect();
        const reasonText = isDisabled && disableRowSelectionReason
            ? typeof disableRowSelectionReason === 'string'
                ? disableRowSelectionReason
                : disableRowSelectionReason(row.original)
            : '';
        const control = isSingleSelect ? (_jsx(RadioGroup, { value: row.getIsSelected(), onChange: row.getToggleSelectedHandler(), children: _jsx(RadioGroupOption, { option: { value: true, label: '' }, disabled: isDisabled }) })) : (_jsx(Checkbox, { dataTestId: `nucleus-table--сheckbox-${row.id}`, hideLabel: true, checked: row.getIsSelected(), label: localizeMessage(new Message('Select row')), onChange: row.getToggleSelectedHandler(), disabled: isDisabled }));
        return (_jsx("td", { className: CONTROL_CELL_CLASSES, children: reasonText ? (_jsx(Tooltip, { as: 'div', title: reasonText, placement: 'top-start', children: control })) : (control) }));
    }, [isSelectable, isSingleSelect, disableRowSelectionReason, localizeMessage]);
    const renderExpandCell = useCallback((row) => {
        var _a;
        if (!hasExpandableRows || !onToggleExpand) {
            return null;
        }
        const isExpanded = (_a = expandedRowIds === null || expandedRowIds === void 0 ? void 0 : expandedRowIds.has(row.id)) !== null && _a !== void 0 ? _a : false;
        const hasContent = rowHasExpandableContent ? rowHasExpandableContent(row.original) : false;
        return (_jsx("td", { className: CONTROL_CELL_CLASSES, children: hasContent ? (_jsx("button", { type: 'button', onClick: () => onToggleExpand(row.id), "aria-expanded": isExpanded, "aria-controls": `nucleus-table--expanded-content-${row.id}`, className: 'nucleus-flex nucleus-items-center nucleus-justify-center nucleus-w-full nucleus-h-full nucleus-cursor-pointer nucleus-bg-transparent nucleus-border-0 nucleus-p-0', "data-testid": `nucleus-table--expand-button-${row.id}`, children: _jsx("span", { className: `nucleus-transition-transform nucleus-duration-200 ${isExpanded ? 'nucleus-rotate-180' : ''}`, children: _jsx(Icon, { name: 'chevronDown', size: 'base' }) }) })) : null }));
    }, [hasExpandableRows, onToggleExpand, expandedRowIds, rowHasExpandableContent]);
    const itemContent = useCallback((_index, item) => {
        var _a, _b;
        if (item.kind === 'skeleton') {
            return renderSkeletonCells();
        }
        if (item.kind === 'expanded') {
            const { row } = item;
            const isContentLoading = (_a = loadingRowIds === null || loadingRowIds === void 0 ? void 0 : loadingRowIds.has(row.id)) !== null && _a !== void 0 ? _a : false;
            const content = (_b = asyncContentMap === null || asyncContentMap === void 0 ? void 0 : asyncContentMap.get(row.id)) !== null && _b !== void 0 ? _b : getStaticExpandableContent(row, expandableContentField);
            return (_jsx("td", { colSpan: colSpan, id: `nucleus-table--expanded-content-${row.id}`, className: `nucleus-box-border nucleus-border-solid nucleus-border-0 nucleus-border-b-1 nucleus-border-neutral-300 nucleus-px-6 nucleus-py-2 ${getExpandedContentClasses ? getExpandedContentClasses(row.original) : ''}`, children: _jsx("div", { className: 'nucleus-overflow-hidden', children: isContentLoading ? (_jsx("div", { className: 'nucleus-flex nucleus-justify-center nucleus-items-center nucleus-py-8', children: _jsx(Spinner, { size: 'base' }) })) : (content) }) }));
        }
        const { row } = item;
        return (_jsxs(_Fragment, { children: [renderExpandCell(row), renderSelectionCell(row), row.getVisibleCells().map((cell, cellIndex) => {
                    var _a;
                    const isFirstDataCell = cellIndex === 0;
                    const pinned = cell.column.getIsPinned();
                    const pinnedStyle = pinned === 'left'
                        ? {
                            position: 'sticky',
                            left: (_a = leftPinOffsets.get(cell.column.id)) !== null && _a !== void 0 ? _a : 0,
                            zIndex: 1,
                            background: 'inherit',
                        }
                        : undefined;
                    return (_jsx("td", { style: autoColumnWidths
                            ? pinnedStyle
                            : Object.assign({ width: cell.column.getSize(), minWidth: cell.column.columnDef.minSize }, pinnedStyle), className: `${CELL_CLASSES} ${isFirstDataCell
                            ? 'nucleus-font-medium nucleus-text-text-primary'
                            : 'nucleus-font-normal nucleus-text-text-secondary'}`, children: flexRender(cell.column.columnDef.cell, cell.getContext()) }, cell.id));
                })] }));
    }, [
        colSpan,
        renderSkeletonCells,
        renderExpandCell,
        renderSelectionCell,
        loadingRowIds,
        asyncContentMap,
        expandableContentField,
        getExpandedContentClasses,
        autoColumnWidths,
        leftPinOffsets,
    ]);
    const components = useMemo(() => ({
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Table: (_a) => {
            var { style, context: _context } = _a, props = __rest(_a, ["style", "context"]);
            return (_jsx("table", Object.assign({}, props, { style: Object.assign(Object.assign({}, style), { width: '100%', tableLayout: autoColumnWidths ? 'auto' : 'fixed' }), className: `nucleus-box-border nucleus-w-full ${autoColumnWidths ? 'nucleus-table-auto' : 'nucleus-table-fixed'} nucleus-border-separate nucleus-border-spacing-0 nucleus-border-solid nucleus-indent-0 nucleus-font-sans nucleus-border-0` })));
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        TableRow: (_a) => {
            var { item, context: _context } = _a, props = __rest(_a, ["item", "context"]);
            if (item.kind === 'skeleton') {
                return (_jsx("tr", Object.assign({}, props, { "data-testid": 'nucleus-table--loading', className: 'nucleus-box-border nucleus-border-solid nucleus-border-0 nucleus-border-current nucleus-bg-white' })));
            }
            if (item.kind === 'expanded') {
                return (_jsx("tr", Object.assign({}, props, { "data-testid": `nucleus-table--expanded-row-${item.row.id}`, className: 'nucleus-box-border nucleus-border-solid nucleus-border-0 nucleus-border-current' })));
            }
            const { row } = item;
            const isDisabled = isSelectable && !row.getCanSelect();
            const isHighlighted = highlightedRowIds.includes(row.id);
            const backgroundClasses = isDisabled
                ? 'nucleus-bg-neutral-50'
                : row.getIsSelected() || isHighlighted
                    ? 'nucleus-bg-primary-50'
                    : 'nucleus-bg-white hover:nucleus-bg-neutral-50';
            return (_jsx("tr", Object.assign({}, props, { "data-testid": `nucleus-table--row-${row.id}`, className: `nucleus-box-border nucleus-w-full nucleus-border-solid nucleus-border-0 nucleus-border-current ${backgroundClasses} ${getRowCustomClasses ? getRowCustomClasses(row.original) : ''}` })));
        },
        EmptyPlaceholder: () => _jsx(TableRowHasNoResults, { colSpan: colSpan, label: labelNoResults }),
    }), [isSelectable, highlightedRowIds, getRowCustomClasses, colSpan, labelNoResults, autoColumnWidths]);
    const fixedHeaderContent = useCallback(() => (_jsx(TableHeadRow, { isSelectable: isSelectable, roundedBorder: false, flatHeaders: table.getFlatHeaders(), enableSorting: enableSorting, enableExpandableRows: hasExpandableRows, autoColumnWidth: autoColumnWidths })), [table, isSelectable, enableSorting, hasExpandableRows, autoColumnWidths]);
    const computeItemKey = useCallback((_index, item) => (item.kind === 'skeleton' ? item.id : `${item.kind}-${item.row.id}`), []);
    return (_jsx("div", { "data-testid": 'nucleus-table--virtual-container', className: `nucleus-box-border nucleus-overflow-hidden nucleus-rounded-lg nucleus-border nucleus-border-solid nucleus-border-neutral-300 ${customScrollParent ? '' : 'nucleus-h-full'}`, children: _jsx(TableVirtuoso, { data: items, computeItemKey: computeItemKey, components: components, fixedHeaderContent: fixedHeaderContent, itemContent: itemContent, endReached: handleEndReached, increaseViewportBy: { top: overscanDistance, bottom: Math.max(overscanDistance, loadMoreThreshold) }, customScrollParent: customScrollParent, style: customScrollParent ? undefined : { height: '100%' } }) }));
};
//# sourceMappingURL=virtual_table_body.js.map